/**
 * Stockists Redesign - Filtering & Interaction Logic
 * 
 * Handles switching between Main Locations (AU/INTL) 
 * and Sub-location filters (States/Countries).
 * Uses Swiper for mobile pill carousel.
 */

class StockistsRedesign {
  constructor() {
    this.container = document.querySelector('[data-stockists-section]');
    if (!this.container) return;

    this.mainTabs = this.container.querySelectorAll('[data-main-tab]');
    this.subPills = this.container.querySelectorAll('[data-sub-pill]');
    this.pillSlides = this.container.querySelectorAll('.c-stockists-v2__pill-slide');
    this.grid = this.container.querySelector('[data-stockists-grid]');
    this.cards = this.container.querySelectorAll('[data-card-sub-ids]');
    this.noResults = this.container.querySelector('[data-no-results]');
    this.swiperEl = this.container.querySelector('[data-sub-carousel]');
    
    this.onlineFilterWrapper = this.container.querySelector('[data-online-filter-wrapper]');
    this.onlineCheckbox = this.container.querySelector('[data-online-checkbox]');

    this.currentMain = 'Australia';
    this.currentSubId = null;
    this.swiper = null;
    this.mobileBreakpoint = 749;

    this.init();
  }

  init() {
    this.bindEvents();
    this.filterPillSlides();
    this.initSwiper();

    // Set initial state based on section settings
    const initialView = this.container.dataset.initialView;

    const visiblePills = Array.from(this.subPills).filter(p => p.offsetParent !== null && p.dataset.parentLocation === this.currentMain);

    if (visiblePills.length > 1) {
      if (initialView === 'all') {
        this.showAllCards();
      } else {
        const firstPill = visiblePills.find(p => p.dataset.subPill !== 'all');
        if (firstPill) {
          this.switchSub(firstPill.dataset.subPill, true);
        }
      }
    } else {
      // Single sub-location: Show all cards (pills inactive) and keep online filter hidden
      this.showAllCards(true);
    }
  }

  initSwiper() {
    if (typeof Swiper === 'undefined' || !this.swiperEl) return;

    if (!this.swiper) {
      this.swiper = new Swiper(this.swiperEl, {
        slidesPerView: 'auto',
        cssMode: true,
        spaceBetween: 0,
        centerInsufficientSlides: true,
        watchOverflow: true,
        mousewheel: {
          forceToAxis: true,
        },
        slideToClickedSlide: false,
        preventClicks: false,
        preventClicksPropagation: false,
        scrollbar: {
          el: '.swiper-scrollbar',
          draggable: true,
          hide: false,
        },
        navigation: {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        },
      });
    }
  }

  filterPillSlides() {
    this.pillSlides.forEach(slide => {
      const isVisible = slide.dataset.parentLocation === this.currentMain;
      slide.style.display = isVisible ? '' : 'none';
    });

    // Update swiper after filtering
    if (this.swiper) {
      this.swiper.update();
      this.swiperEl.classList.toggle('is-locked', this.swiper.isLocked);
    }
  }

  showAllCards(isInitial = false) {
    this.currentSubId = 'all';

    // Update Pill UI for the current main location
    this.subPills.forEach(pill => {
      const isAllPillForCurrentMain = pill.dataset.subPill === 'all' && pill.dataset.parentLocation === this.currentMain;
      pill.classList.toggle('is-active', isAllPillForCurrentMain);
    });
    
    // Show/Hide the online toggle filter based on whether multiple sub-locations exist
    if (this.onlineFilterWrapper) {
      const visiblePills = Array.from(this.subPills).filter(p => p.offsetParent !== null && p.dataset.parentLocation === this.currentMain);
      if (visiblePills.length > 1) {
        this.onlineFilterWrapper.classList.add('is-expanded');
      } else {
        this.onlineFilterWrapper.classList.remove('is-expanded');
      }
    }

    // Show all cards for the CURRENT main location
    let hasResults = false;
    const activeMain = this.currentMain.toLowerCase();
    const isOnlineOnly = this.onlineCheckbox ? this.onlineCheckbox.checked : false;

    this.cards.forEach(card => {
      const cardMainLocs = (card.dataset.cardMainLocations || "").toLowerCase();
      let isVisible = cardMainLocs.includes(activeMain) || cardMainLocs.trim() === "";

      if (isVisible && isOnlineOnly) {
        const badges = (card.dataset.cardBadges || "").split(',');
        if (!badges.includes('online')) {
          isVisible = false;
        }
      }

      card.style.display = isVisible ? 'flex' : 'none';
      if (isVisible) {
        hasResults = true;
        card.classList.add('is-visible');
      } else {
        card.classList.remove('is-visible');
      }
    });

    this.noResults.style.display = hasResults ? 'none' : 'block';

    if (this.swiper) {
      this.swiper.update();
      this.swiperEl.classList.toggle('is-locked', this.swiper.isLocked);
    }
  }

  bindEvents() {
    // Main Tab Switching
    this.mainTabs.forEach(tab => {
      tab.addEventListener('click', (e) => {
        const target = e.currentTarget.dataset.mainTab;
        this.switchMain(target);
      });
    });

    // Sub-pill Switching
    this.subPills.forEach(pill => {
      pill.addEventListener('click', (e) => {
        const subId = e.currentTarget.dataset.subPill;
        this.switchSub(subId);
      });
    });

    // Online Store Toggle
    if (this.onlineCheckbox) {
      this.onlineCheckbox.addEventListener('change', () => {
        // Re-run the active filter mode
        if (this.currentSubId && this.currentSubId !== 'all') {
          this.filterCards();
        } else {
          this.showAllCards();
        }
      });
    }
  }

  switchMain(location) {
    if (this.currentMain === location) return;

    this.currentMain = location;

    // Update Tab UI
    this.mainTabs.forEach(tab => {
      tab.classList.toggle('is-active', tab.dataset.mainTab === location);
    });

    // Update Pill/Slide Visibility
    this.filterPillSlides();

    // Show all cards for new location or select first pill
    const initialView = this.container.dataset.initialView;
    if (initialView === 'all') {
      this.showAllCards();
    } else {
      const firstPill = Array.from(this.subPills).find(p => p.dataset.parentLocation === location);
      if (firstPill) {
        this.switchSub(firstPill.dataset.subPill);
      }
    }

    // Reset swiper position
    if (this.swiper) {
      this.swiper.slideTo(0);
      this.swiper.update();
      this.swiperEl.classList.toggle('is-locked', this.swiper.isLocked);
    }
  }

  switchSub(subId, isInitial = false) {
    if (subId === 'all') {
      this.showAllCards(isInitial);
      return;
    }

    this.currentSubId = subId;

    // Update Pill UI
    this.subPills.forEach(pill => {
      pill.classList.toggle('is-active', pill.dataset.subPill === subId);
    });

    // Expand/Collapse the online toggle filter
    if (this.onlineFilterWrapper) {
      const visiblePills = Array.from(this.subPills).filter(p => p.offsetParent !== null && p.dataset.parentLocation === this.currentMain);
      if (visiblePills.length > 1 && !isInitial) {
        this.onlineFilterWrapper.classList.add('is-expanded');
      } else {
        this.onlineFilterWrapper.classList.remove('is-expanded');
      }
    }

    this.filterCards();
  }

  filterCards() {
    let matchCount = 0;
    const isOnlineOnly = this.onlineCheckbox ? this.onlineCheckbox.checked : false;

    this.cards.forEach(card => {
      const subIds = card.dataset.cardSubIds.split(',').map(id => id.trim());
      let isMatch = subIds.includes(this.currentSubId);

      if (isMatch && isOnlineOnly) {
        const badges = (card.dataset.cardBadges || "").split(',');
        if (!badges.includes('online')) {
          isMatch = false;
        }
      }

      if (isMatch) {
        card.style.display = 'flex';
        card.classList.add('is-visible');
        matchCount++;
      } else {
        card.style.display = 'none';
        card.classList.remove('is-visible');
      }
    });

    if (this.noResults) {
      this.noResults.style.display = matchCount === 0 ? 'block' : 'none';
    }
  }
}

// Initialize on DOM load or Shopify Section Load
document.addEventListener('DOMContentLoaded', () => {
  new StockistsRedesign();
});

// Re-init if Shopify theme editor updates
document.addEventListener('shopify:section:load', () => {
  new StockistsRedesign();
});
